import { Children, isValidElement, useMemo, type ReactNode } from "react";
import ReactMarkdown, { type Components } from "react-markdown";

import { blogHeadings, plainHeadingText } from "../utils/headings";
import { blogSlugify } from "@complete-athlete/enums";
import { cn } from "@/lib/utils";
import { getUrlOrigin } from "@/paraglide/runtime.js";

/** The words inside rendered children, for a heading's anchor. */
const textOf = (children: ReactNode): string =>
  Children.toArray(children)
    .map((child) => {
      if (typeof child === "string" || typeof child === "number") return String(child);
      if (isValidElement<{ children?: ReactNode }>(child)) return textOf(child.props.children);
      return "";
    })
    .join("");

/** A link off this site opens in a new tab. Decided from the public origin, the same on the server and in the browser. */
const isExternal = (href: string | undefined) =>
  Boolean(href && /^https?:\/\//i.test(href) && !href.startsWith(getUrlOrigin()));

/** A heading with an anchor a reader can link to, and the `#` that copies it on hover. */
const Heading = ({ as: Tag, id, children }: { as: "h2" | "h3"; id: string; children: ReactNode }) => (
  <Tag
    id={id}
    className={cn(
      "group/heading relative scroll-mt-28 font-medium text-balance text-foreground",
      Tag === "h2"
        ? "mt-16 mb-5 text-[clamp(1.5rem,2.6vw,2rem)] leading-[1.12] tracking-[-0.035em]"
        : "mt-11 mb-4 text-[1.3rem] leading-[1.2] tracking-[-0.025em]",
    )}
  >
    <a
      href={`#${id}`}
      aria-hidden
      tabIndex={-1}
      className="absolute top-0 -left-6 hidden pr-2 text-muted-foreground/50 opacity-0 transition-opacity group-hover/heading:opacity-100 hover:text-[var(--site-ink)] lg:block"
    >
      #
    </a>
    {children}
  </Tag>
);

/**
 * A post's body, set for reading: a wide measure of 18px type, sub-headings with anchors that the
 * table of contents follows, pictures as figures with their caption (the Markdown image title),
 * pull quotes, code and rules. Raw HTML never renders.
 */
export const BlogMarkdown = ({ content, className }: { content: string; className?: string }) => {
  // Each heading's id comes from its line, as the table of contents worked it out, so the two
  // agree however often (and in whatever order) React renders the headings.
  const idByLine = useMemo(() => new Map(blogHeadings(content).map((heading) => [heading.line, heading.id])), [content]);
  const idOf = (line: number | undefined, children: ReactNode) =>
    (line ? idByLine.get(line) : undefined) ?? (blogSlugify(plainHeadingText(textOf(children))) || "section");

  const components: Components = {
    h1: ({ node, children }) => (
      <Heading as="h2" id={idOf(node?.position?.start.line, children)}>
        {children}
      </Heading>
    ),
    h2: ({ node, children }) => (
      <Heading as="h2" id={idOf(node?.position?.start.line, children)}>
        {children}
      </Heading>
    ),
    h3: ({ node, children }) => (
      <Heading as="h3" id={idOf(node?.position?.start.line, children)}>
        {children}
      </Heading>
    ),
    h4: ({ children }) => <h4 className="mt-9 mb-3 text-lg font-medium tracking-[-0.02em]">{children}</h4>,
    // A paragraph holding only a picture is the picture: a figure cannot sit inside a `<p>`.
    p: ({ node, children }) => {
      const only = node?.children.length === 1 ? node.children[0] : undefined;
      if (only?.type === "element" && only.tagName === "img") return <>{children}</>;
      return <p className="my-6 text-pretty">{children}</p>;
    },
    img: ({ src, alt, title }) => (
      <figure className="my-12 lg:-mx-12">
        <img
          src={typeof src === "string" ? src : undefined}
          alt={alt ?? ""}
          loading="lazy"
          decoding="async"
          // A tall picture keeps its shape but never takes more than most of the screen.
          className="mx-auto max-h-[min(78vh,780px)] w-auto max-w-full rounded-[22px] border border-border bg-muted"
        />
        {title ? (
          <figcaption className="mx-auto mt-3.5 max-w-[56ch] text-center text-sm leading-relaxed text-muted-foreground">
            {title}
          </figcaption>
        ) : null}
      </figure>
    ),
    a: ({ href, children }) => {
      const external = isExternal(href);
      return (
        <a
          href={href}
          {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          className="font-medium text-foreground underline decoration-[var(--site-fog)] decoration-2 underline-offset-[5px] transition-colors hover:decoration-primary"
        >
          {children}
        </a>
      );
    },
    strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
    ul: ({ children }) => <ul className="my-6 space-y-2.5 pl-6 marker:text-[var(--site-ink)] [list-style:disc]">{children}</ul>,
    ol: ({ children }) => (
      <ol className="my-6 space-y-2.5 pl-6 marker:font-mono marker:text-[0.85em] marker:text-muted-foreground [list-style:decimal]">
        {children}
      </ol>
    ),
    li: ({ children }) => <li className="pl-1.5">{children}</li>,
    // A pull quote: bigger words with an opening mark, set apart by space rather than a rule.
    blockquote: ({ children }) => (
      <blockquote className="relative my-12 px-2 text-[clamp(1.35rem,2.2vw,1.7rem)] leading-[1.3] font-medium tracking-[-0.025em] text-balance text-foreground [&_p]:my-0 md:px-10">
        <span aria-hidden className="mb-2 block font-serif text-6xl leading-none text-primary">
          “
        </span>
        {children}
      </blockquote>
    ),
    hr: () => (
      <div role="separator" className="my-14 flex justify-center gap-3 text-[var(--site-ink)]">
        <span className="size-1.5 rounded-full bg-current opacity-60" />
        <span className="size-1.5 rounded-full bg-current opacity-60" />
        <span className="size-1.5 rounded-full bg-current opacity-60" />
      </div>
    ),
    pre: ({ children }) => (
      <pre className="my-8 overflow-x-auto rounded-2xl border border-border bg-[var(--site-tint)] p-5 font-mono text-[13.5px] leading-relaxed [&_code]:bg-transparent [&_code]:p-0 [&_code]:text-[length:inherit]">
        {children}
      </pre>
    ),
    code: ({ children }) => (
      <code className="rounded-md bg-[var(--site-tint)] px-1.5 py-0.5 font-mono text-[0.86em] text-foreground">{children}</code>
    ),
  };

  return (
    <div className={cn("text-[1.125rem] leading-[1.75] text-foreground/85 [&>*:first-child]:mt-0", className)}>
      <ReactMarkdown skipHtml components={components}>
        {content}
      </ReactMarkdown>
    </div>
  );
};
